import React from "react";
import { useLang } from "../lib/LangContext";

const LEVEL_COLORS = ["#9E0E40", "#FF9900", "#B58900", "#FF76B7", "#8A0E9E"];

/**
 * StaircaseViz — Ariovis 5-tier maturity ladder.
 *
 * variant="steps"  → clean staircase with numbered blocks aligned to a baseline
 * variant="ladder" → vertical stack (compact card in hero)
 * Labels never overflow; long phase names wrap.
 */
export default function StaircaseViz({
  current = 0,
  target = 0,
  variant = "steps",
  height,
  showLabels = true,
}) {
  const { t } = useLang();
  const levels = [
    { level: 1, name: t("method.level.1.title"), short: t("method.level.1.short") },
    { level: 2, name: t("method.level.2.title"), short: t("method.level.2.short") },
    { level: 3, name: t("method.level.3.title"), short: t("method.level.3.short") },
    { level: 4, name: t("method.level.4.title"), short: t("method.level.4.short") },
    { level: 5, name: t("method.level.5.title"), short: t("method.level.5.short") },
  ];

  if (variant === "ladder") {
    return <LadderViz levels={levels} current={current} target={target} />;
  }

  const baseH = height || 240;
  return (
    <div className="w-full" data-testid="staircase-viz">
      <div className="grid grid-cols-5 gap-2 md:gap-3 items-end" style={{ height: baseH }}>
        {levels.map((l) => {
          const h = Math.round(baseH * (l.level / 5));
          const color = LEVEL_COLORS[l.level - 1];
          const isCurrent = l.level === current;
          const isTarget = l.level === target && target !== current;
          const isReached = l.level <= current;
          return (
            <div key={l.level} className="relative flex flex-col justify-end" data-testid={`staircase-step-${l.level}`}>
              <div
                className="w-full rounded-t-lg relative overflow-hidden border-t border-l border-r"
                style={{
                  height: h,
                  background: isReached ? color : "#f1f5f9",
                  borderColor: isReached ? color : "#e2e8f0",
                }}
              >
                {isTarget && (
                  <div
                    className="absolute inset-0 rounded-t-lg border-2"
                    style={{ borderColor: color }}
                  />
                )}
                <div className="absolute top-2 left-2 text-[10px] font-bold tracking-widest opacity-70" style={{ color: isReached ? "#fff" : "#94a3b8" }}>
                  0{l.level}
                </div>
                {isCurrent && (
                  <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] font-bold tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-white/90 text-slate-900">
                    {t("results.level_current_badge")}
                  </span>
                )}
                {isTarget && (
                  <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] font-bold tracking-[0.15em] px-1.5 py-0.5 rounded-full text-white" style={{ background: color }}>
                    {t("results.level_target_badge")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showLabels && (
        <div className="grid grid-cols-5 gap-2 md:gap-3 mt-3">
          {levels.map((l) => (
            <div key={l.level} className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: LEVEL_COLORS[l.level - 1] }}>
                {l.short}
              </div>
              <div className="mt-0.5 text-xs md:text-sm font-semibold text-slate-900 leading-tight break-words">
                {l.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LadderViz({ levels, current, target }) {
  const { t } = useLang();
  return (
    <div className="space-y-2" data-testid="staircase-ladder">
      {levels.map((l, idx) => {
        const color = LEVEL_COLORS[l.level - 1];
        const isCurrent = l.level === current;
        const isTarget = l.level === target && target !== current;
        const isReached = l.level <= current;
        return (
          <div
            key={l.level}
            className={`relative flex items-center gap-3 pl-2 pr-3 py-2.5 rounded-lg transition-colors ${
              isReached ? "bg-slate-900 text-white" : "bg-slate-50"
            }`}
            style={
              isTarget
                ? { border: `1.5px dashed ${color}`, background: "white" }
                : undefined
            }
            data-testid={`ladder-step-${l.level}`}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{
                background: isReached ? color : "white",
                color: isReached ? "white" : color,
                border: `1.5px solid ${color}`,
              }}
            >
              {l.level}
            </div>
            <div className="min-w-0 flex-1">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${isReached ? "opacity-80" : "text-slate-500"}`}>
                {l.short}
              </div>
              <div className={`text-sm font-semibold leading-tight truncate ${isReached ? "text-white" : "text-slate-900"}`}>
                {l.name}
              </div>
            </div>
            {isCurrent && (
              <span className="text-[9px] font-bold tracking-[0.15em] px-2 py-1 rounded-full bg-white/95 text-slate-900 flex-shrink-0">
                {t("results.level_current_badge")}
              </span>
            )}
            {isTarget && (
              <span className="text-[9px] font-bold tracking-[0.15em] px-2 py-1 rounded-full text-white flex-shrink-0" style={{ background: color }}>
                {t("results.level_target_badge")}
              </span>
            )}
            <div
              className="absolute left-6 top-full h-2 w-px"
              style={{ background: idx < levels.length - 1 ? "#e2e8f0" : "transparent" }}
              aria-hidden
            />
          </div>
        );
      })}
    </div>
  );
}
